import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface Card {
  id: number;
  name: string;
  cardNumber: string;
  type: string;
  rarity: string;
  imageUrl?: string;
  imageBlobPath?: string;
  power?: number;
  cost?: number;
  gem?: number;
  gemColor?: string;
  color?: string;
  symbol?: string;
  soi?: string;
}

interface CardEdit extends Card {
  modified?: boolean;
}

interface User {
  role?: string;
}

const AdminCardEditPage = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState<CardEdit[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const limit = 20;

  useEffect(() => {
    checkAdminAccess();
    fetchCards();
  }, [page, searchTerm, sortBy, sortOrder]);

  const checkAdminAccess = () => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (!token) {
      setMessage({ type: 'error', text: 'Please login to access this page' });
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    if (userStr) {
      try {
        const user: User = JSON.parse(userStr);
        if (user.role !== 'admin') {
          setMessage({ type: 'error', text: 'Admin access required' });
          setTimeout(() => navigate('/dashboard'), 2000);
          return;
        }
      } catch (error) {
        console.error('Failed to parse user:', error);
        navigate('/login');
      }
    }
  };

  const fetchCards = async () => {
    try {
      setLoading(true);
      setMessage(null);

      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await axios.get(`${API_URL}/cards?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success && response.data.data) {
        setCards(response.data.data.map((card: Card) => ({ ...card, modified: false })));
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.totalPages);
        }
      }
    } catch (error: any) {
      console.error('Failed to fetch cards:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load cards'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (cardId: number, field: keyof Card, value: any) => {
    setCards(prevCards =>
      prevCards.map(card =>
        card.id === cardId
          ? { ...card, [field]: value, modified: true }
          : card
      )
    );
  };

  const handleBulkSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      const modifiedCards = cards.filter(card => card.modified);

      if (modifiedCards.length === 0) {
        setMessage({ type: 'error', text: 'No changes to save' });
        return;
      }

      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

      const updates = modifiedCards.map(card => {
        const { modified, ...cardData } = card;
        return {
          id: cardData.id,
          power: cardData.power,
          cost: cardData.cost,
          gem: cardData.gem,
          gemColor: cardData.gemColor,
          color: cardData.color,
          symbol: cardData.symbol,
          soi: cardData.soi,
        };
      });

      const response = await axios.patch(
        `${API_URL}/cards/bulk-update`,
        { updates },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: `Successfully updated ${response.data.data.success} cards`
        });
        // Refresh the card list
        fetchCards();
      }
    } catch (error: any) {
      console.error('Failed to save changes:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save changes'
      });
    } finally {
      setSaving(false);
    }
  };

  const modifiedCount = cards.filter(card => card.modified).length;

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Admin - Bulk Edit Cards</h1>

      {message && (
        <div className={`mb-4 p-4 rounded ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {message.text}
        </div>
      )}

      {/* Search and Actions */}
      <div className="mb-6 space-y-4">
        <div className="flex gap-4 items-center">
          <input
            type="text"
            placeholder="Search cards by name..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleBulkSave}
            disabled={saving || modifiedCount === 0}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : `Save Changes (${modifiedCount})`}
          </button>
        </div>

        {/* Sort Controls */}
        <div className="flex gap-4 items-center">
          <label className="text-sm font-medium text-gray-700">Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="name">Name</option>
            <option value="cardNumber">Card Number</option>
            <option value="type">Type</option>
            <option value="rarity">Rarity</option>
            <option value="setName">Set Name</option>
            <option value="power">Power</option>
            <option value="cost">Cost</option>
            <option value="gem">Gem</option>
            <option value="color">Color</option>
            <option value="symbol">Symbol</option>
            <option value="soi">SOI</option>
            <option value="createdAt">Created Date</option>
            <option value="updatedAt">Updated Date</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center gap-2"
            title={`Sort ${sortOrder === 'ASC' ? 'Ascending' : 'Descending'}`}
          >
            <span>{sortOrder === 'ASC' ? '↑' : '↓'}</span>
            <span>{sortOrder === 'ASC' ? 'Ascending' : 'Descending'}</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading cards...</div>
      ) : (
        <>
          {/* Cards Table */}
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" style={{ width: '210px', minWidth: '210px' }}>Image</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Number</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rarity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Power</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gem</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gem Color</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Color</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Symbol</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SOI</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {cards.map((card) => (
                  <tr key={card.id} className={card.modified ? 'bg-yellow-50' : ''} style={{ height: '280px' }}>
                    <td className="px-4 py-4" style={{ width: '210px', minWidth: '210px' }}>
                      {(card.imageUrl || card.imageBlobPath) ? (
                        <img
                          src={card.imageUrl || card.imageBlobPath}
                          alt={card.name}
                          className="w-48 h-64 object-contain rounded shadow-md"
                        />
                      ) : (
                        <div className="w-48 h-64 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-500">
                          No image
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm align-middle">{card.name}</td>
                    <td className="px-4 py-4 text-sm align-middle">{card.cardNumber}</td>
                    <td className="px-4 py-4 text-sm align-middle">{card.type}</td>
                    <td className="px-4 py-4 text-sm align-middle">{card.rarity}</td>
                    <td className="px-4 py-4 align-middle">
                      <input
                        type="number"
                        value={card.power || ''}
                        onChange={(e) => handleFieldChange(card.id, 'power', e.target.value ? parseInt(e.target.value) : null)}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Power"
                      />
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <input
                        type="number"
                        value={card.cost || ''}
                        onChange={(e) => handleFieldChange(card.id, 'cost', e.target.value ? parseInt(e.target.value) : null)}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <input
                        type="number"
                        value={card.gem || ''}
                        onChange={(e) => handleFieldChange(card.id, 'gem', e.target.value ? parseInt(e.target.value) : null)}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <input
                        type="text"
                        value={card.gemColor || ''}
                        onChange={(e) => handleFieldChange(card.id, 'gemColor', e.target.value || null)}
                        className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Color"
                      />
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <input
                        type="text"
                        value={card.color || ''}
                        onChange={(e) => handleFieldChange(card.id, 'color', e.target.value || null)}
                        className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Color"
                      />
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <input
                        type="text"
                        value={card.symbol || ''}
                        onChange={(e) => handleFieldChange(card.id, 'symbol', e.target.value || null)}
                        className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Symbol"
                      />
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <input
                        type="text"
                        value={card.soi || ''}
                        onChange={(e) => handleFieldChange(card.id, 'soi', e.target.value || null)}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="SOI"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-6 flex justify-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-4 py-2">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminCardEditPage;
